<script setup lang="ts">
    import { useStory } from '~/composables/storyblok-proxy/useStory';
    import type { Story } from '~/utils/sammy/types';
    import type { StoryPage } from '~/types/storyPage';

    defineOptions({ name: 'PageResolver' });

    definePageMeta({
        middleware: ['academy-consent-check', 'docebo-session']
    });

    const { lang } = useLanguage();

    const { rawPath } = useCleanStoryblokPath();
    /**
     * We intentionally break the reactivity here by accessing rawPath as a
     * value. This prevents issues when navigating from [...slug].vue to any
     * hardcoded route like [country]/account/...
     *
     * The issue occurs because rawPath would update before the navigation is
     * completed causing useStory to fetch the slug of /account and not
     * being able to find it in Storyblok, which is technically correct because
     * it's a hardcoded route and not a Storyblok page.
     */
    const res = await useStory<PageComponentStoryblok>(rawPath.value, null, { disableFatalErrors: true });

    if (res.error?.value && res.error.value.statusCode) {
        throw createError({
            ...res.error.value,
            fatal: import.meta.client || res.error.value.statusCode >= 500
        });
    }

    const story = res.data;

    const { context: platformContext } = usePlatformMeta();
    const { setFromStoryblokLink } = useGlobalRedirectTarget();
    const redirectTargetLink = story.value?.content?.settings?.[0]?.redirectTarget;

    if (redirectTargetLink) {
        setFromStoryblokLink(redirectTargetLink, platformContext.value);
    }

    useLocalizedRedirectOnLangSwitch(story.value?.translated_slugs);

    useStoryblokBridgeRef(story);

    const storyPage = useState<StoryPage>('story-page', () => ({
        uuid: (story.value as Story)?.uuid ?? '',
        slug: rawPath.value
    }));

    /**
     * `immediate`, because on client-side navigation the state still holds the
     * previous page (the initializer above only runs once) and `story` is
     * already loaded at this point, so a lazy watcher would never fire.
     */
    watch(
        story,
        (newStory) => {
            if (newStory) {
                storyPage.value = {
                    uuid: newStory.uuid ?? '',
                    slug: rawPath.value
                };
            }
        },
        { immediate: true }
    );

    const indexSettings = computed(() => {
        const robots = story.value?.content?.settings?.[0]?.robots || 'index';
        switch (robots) {
            case 'noindex':
                return 'noindex, nofollow';
            case 'index':
                return 'index, follow';
            default:
                return robots;
        }
    });

    const head = computed(() => ({
        meta: [
            {
                name: 'description',
                content: story.value?.content.seo?.description || 'MEDICE Health Family'
            },
            {
                name: 'robots',
                content: indexSettings.value
            }
        ],
        htmlAttrs: {
            lang: lang.value
        }
    }));

    useHead(head);

    usePageTitle();
</script>

<template>
    <StoryblokComponentWrapper
        v-if="story"
        :blok="story.content"
    />
</template>
